import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface NormalizedError {
  status: number;
  code?: string;
  message?: string;
  details?: any;
  raw?: any;
}

@Injectable()
export class ErrorNormalizerInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: any) => {
        const normalized: NormalizedError = { status: 0, raw: err } as NormalizedError;
        if (err instanceof HttpErrorResponse) {
          normalized.status = err.status || 0;

          // Handle specific server errors (500+) with generic messages
          if (normalized.status >= 500) {
            normalized.message = this.handleServerError(err);
          }
          // Try to extract message based on error structure for other status codes
          else if (err.error) {
            if (typeof err.error === 'string') {
              normalized.message = err.error;
            } else if (typeof err.error === 'object') {
              normalized.message = this.extractErrorMessage(err.error, err);
            } else {
              normalized.message = err.message || 'Ha ocurrido un error';
            }
          } else {
            normalized.message = err.message || 'Ha ocurrido un error en la petición';
          }
        } else if (err && err.message) {
          normalized.message = err.message;
        } else {
          normalized.message = 'Error desconocido';
        }

        return throwError(() => normalized);
      })
    );
  }

  /**
   * Handle server errors (500+) with user-friendly messages
   */
  private handleServerError(err: HttpErrorResponse): string {
    // Check if error contains MongoDB/Mongoose CastError
    if (err.error && typeof err.error === 'object') {
      const errorObj = err.error;

      // Check for nested error structure like {"err": {"name": "CastError", ...}}
      if (errorObj.err && typeof errorObj.err === 'object') {
        const innerError = errorObj.err;

        // MongoDB CastError
        if (innerError.name === 'CastError' || innerError.kind === 'ObjectId') {
          return 'Error en el servidor al procesar los datos. Por favor, verifica la información e intenta nuevamente.';
        }

        // MongoDB ValidationError
        if (innerError.name === 'ValidationError') {
          return 'Error de validación en el servidor. Por favor, verifica los datos ingresados.';
        }

        // MongoDB DuplicateKey
        if (innerError.code === 11000 || innerError.name === 'MongoError') {
          return 'El registro ya existe en el servidor. Por favor, verifica los datos.';
        }
      }

      // Check for direct error properties
      if (errorObj.name === 'CastError' || errorObj.kind === 'ObjectId') {
        return 'Error en el servidor al procesar los datos. Por favor, verifica la información e intenta nuevamente.';
      }

      if (errorObj.name === 'ValidationError') {
        return 'Error de validación en el servidor. Por favor, verifica los datos ingresados.';
      }

      // Check if there's a message property
      if (errorObj.message && typeof errorObj.message === 'string') {
        // Don't show technical MongoDB errors to users
        if (this.isTechnicalError(errorObj.message)) {
          return 'No pudimos procesar tu solicitud';
        }
        return errorObj.message;
      }
    }

    // Default generic message for 500+ errors
    return 'No pudimos procesar tu solicitud';
  }

  /**
   * Extract error message from error object for non-500 errors
   */
  private extractErrorMessage(errorObj: any, httpErr: HttpErrorResponse): string {
    // Check for nested error.error.message format
    if (errorObj.error && errorObj.error.message) {
      return errorObj.error.message;
    }
    // Check for direct message property (most common)
    else if (errorObj.message) {
      return errorObj.message;
    }
    // Check for validation errors
    else if (errorObj.errors) {
      return errorObj.message || 'Errores de validación';
    }
    // Check for detail property
    else if (errorObj.detail) {
      return errorObj.detail;
    }
    // Fallback: stringify the error object
    else {
      try {
        // Check if it's a simple object with just one key-value pair
        const keys = Object.keys(errorObj);
        if (keys.length === 1 && keys[0] === 'message') {
          return errorObj.message;
        } else {
          return JSON.stringify(errorObj).slice(0, 1000);
        }
      } catch (e) {
        return httpErr.message || 'Ha ocurrido un error';
      }
    }
  }

  /**
   * Check if error message is technical and shouldn't be shown to users
   */
  private isTechnicalError(message: string): boolean {
    const technicalPatterns = [
      /cast to objectid failed/i,
      /validation failed/i,
      /duplicate key error/i,
      /mongodb/i,
      /mongoose/i,
      /buffer\./i,
      /stringvalue/i,
      /valuetype/i,
      /at model\./i,
      /at object\./i,
      /\$\w+/,  // MongoDB operators like $set, $push
      /^e11000/i  // MongoDB duplicate key error code
    ];

    return technicalPatterns.some(pattern => pattern.test(message));
  }
}
